import type { Invoice, InvoiceStatus, InvoiceType } from '@/types/invoice'
// Liegt in lib/utils, weil auch das geteilte InlineField darauf zugreift.
export { parseGermanNumber } from '@/lib/utils'

export const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  new:           { label: 'Neu',          color: 'bg-gray-100 text-gray-700' },
  reviewed:      { label: 'Geprüft',      color: 'bg-blue-100 text-blue-700' },
  pay:           { label: 'Bitte zahlen', color: 'bg-emerald-100 text-emerald-700' },
  paid:          { label: 'Bezahlt',      color: 'bg-green-200 text-green-800' },
  clarification: { label: 'Klärung',      color: 'bg-orange-100 text-orange-700' },
  resolved:      { label: 'Erledigt',     color: 'bg-green-100 text-green-700' },
}

export const TYPE_LABEL: Record<string, string> = {
  incoming: 'Eingangsrechnung',
  outgoing: 'Ausgangsrechnung',
  credit_note: 'Gutschrift',
  expense_claim: 'Beleg',
  reminder: 'Mahnung',
}

export const PAYMENT_METHODS = ['Überweisung', 'Bar', 'Karte', 'PayPal']

export const CATEGORY_OPTIONS = [
  'Büromaterial', 'Werkzeug', 'Material', 'Miete', 'Versicherung',
  'Fahrzeug', 'Telefon', 'Software', 'Dienstleistung', 'Sonstiges',
]

/**
 * #86: Eine Mahnung gilt als erledigt, wenn sie direkt bezahlt ('paid') ODER ueber
 * bezahlte Rechnungen abgeschlossen ('resolved') wurde.
 */
export const isReminderDone = (status?: string | null) =>
  status === 'paid' || status === 'resolved'

/**
 * #119 "Wichtige Rechnung": nur Eingangsrechnungen und Mahnungen, und nur solange sie
 * offen sind. Dieselbe Bedingung steckt im ORDER BY von getAllInvoices und im Reset
 * beim Bezahlen — laufen die drei Stellen auseinander, steht ein Stern an einer Zeile,
 * die nicht mehr nach oben sortiert wird.
 */
const IMPORTANT_TYPES: string[] = ['incoming', 'reminder']
export const isImportantEligible = (inv?: { type?: string; status?: string } | null) =>
  !!inv && IMPORTANT_TYPES.includes(inv.type ?? '') &&
  !['paid', 'resolved', 'rejected'].includes(inv.status ?? '')

export const formatCurrency = (amount?: number | null) => {
  if (amount == null) return '-'
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount)
}

/**
 * SQLite liefert Zeitstempel als "YYYY-MM-DD HH:MM:SS" (paid_at, created_at).
 * Safari kann das Leerzeichen nicht parsen und zeigt "Invalid Date" — mit "T"
 * lesen es alle Browser gleich (als Ortszeit, wie Chrome es bisher tat).
 */
const toDate = (dateStr: string) => new Date(/^\d{4}-\d{2}-\d{2} \d/.test(dateStr) ? dateStr.replace(' ', 'T') : dateStr)

export const formatDate = (dateStr?: string | null) => {
  if (!dateStr) return '-'
  return toDate(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: '2-digit',
  })
}

export const formatDateLong = (dateStr?: string | null) => {
  if (!dateStr) return '-'
  return toDate(dateStr).toLocaleDateString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  })
}


/** Heute auf 00:00 — Fristen zaehlen in Tagen, nicht in Stunden. */
function heute(): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

const tageBis = (iso: string) =>
  Math.ceil((new Date(iso).getTime() - heute().getTime()) / 86_400_000)

export interface PaymentDeadline {
  /** Datum, an dem gehandelt werden muss — Skontofrist solange sie laeuft, sonst Faelligkeit. */
  actionDate: string | null
  /** Sortierschluessel: nie leer, damit "sofort faellig" nicht alles nach oben zieht. */
  sortKey: string
  /** Laeuft gerade eine ziehbare Skontofrist? */
  skontoActive: boolean
  skontoPercent: number | null
  skontoAmount: number | null
  dueDate: string | null
  /** Tage bis actionDate (negativ = vorbei). */
  daysLeft: number | null
  /** Ampel: 'over' | 'red' | 'amber' | 'green' | null (nichts zu tun). */
  tone: 'over' | 'red' | 'amber' | 'green' | null
}

/**
 * Zahlungsfrist einer Rechnung — die EINE Antwort auf "wann muss ich zahlen?".
 *
 * Solange die Skontofrist laeuft, ist SIE der Termin: wer sie verpasst, verliert
 * bares Geld, waehrend die Faelligkeit danach noch Wochen Luft hat. Erst wenn sie
 * abgelaufen ist, zaehlt das endgueltige Zahlungsziel (#125/#126).
 *
 * Sortierung UND Anzeige ziehen dieselbe Funktion — laufen sie auseinander,
 * steht die Liste in einer anderen Reihenfolge als das, was der Nutzer liest.
 */
export function getPaymentDeadline(inv: {
  status?: string | null
  type?: string | null
  due_date?: string | null
  invoice_date?: string | null
  skonto_date?: string | null
  skonto_percent?: number | null
  skonto_amount?: number | null
}): PaymentDeadline {
  const closed = ['paid', 'resolved', 'rejected'].includes(inv.status ?? '')
  // Ein gedruckter Skontobetrag zaehlt genauso wie ein Satz (#138): UNI ELEKTRO
  // nennt nur "unter Abzug von 1,19 EUR Skonto" und gar keinen Prozentsatz.
  const skontoOffen = !closed && !!inv.skonto_date
    && (!!inv.skonto_percent || !!inv.skonto_amount) && tageBis(inv.skonto_date) >= 0

  const actionDate = skontoOffen ? inv.skonto_date! : (inv.due_date ?? null)
  const daysLeft = actionDate ? tageBis(actionDate) : null

  let tone: PaymentDeadline['tone'] = null
  if (!closed) {
    if (skontoOffen) {
      // Eigene Staffel: bei Skonto ist nicht "ueberfaellig" das Problem, sondern
      // "in wenigen Tagen ist der Rabatt weg".
      tone = daysLeft! <= 7 ? 'red' : daysLeft! <= 14 ? 'amber' : 'green'
    } else if (!inv.due_date) {
      tone = inv.type === 'reminder' ? null : 'over'
    } else if (daysLeft! < 0) tone = 'over'
    else if (daysLeft! <= 3) tone = 'red'
    else if (daysLeft! <= 7) tone = 'amber'
  }

  return {
    actionDate,
    // Ohne Termin ist der Beleg sofort faellig — das Rechnungsdatum ist dafuer
    // der ehrliche Platzhalter und reiht ihn chronologisch korrekt ein.
    sortKey: actionDate || inv.invoice_date || '',
    skontoActive: skontoOffen,
    skontoPercent: skontoOffen ? (inv.skonto_percent ?? null) : null,
    skontoAmount: skontoOffen ? (inv.skonto_amount ?? null) : null,
    dueDate: inv.due_date ?? null,
    daysLeft,
    tone,
  }
}

/** Farbwerte der Ampel — an einer Stelle, damit Punkt und Text nicht auseinanderlaufen. */
export const DEADLINE_TONE: Record<string, { dot: string; text: string }> = {
  over:  { dot: 'bg-red-500',    text: 'text-red-600' },
  red:   { dot: 'bg-red-500',    text: 'text-red-600' },
  amber: { dot: 'bg-amber-500',  text: 'text-amber-600' },
  green: { dot: 'bg-emerald-500', text: 'text-emerald-700' },
}

/**
 * Skonto-Folgewerte neu berechnen.
 *
 * Lag bis zum Umbau inline in saveEdit(). Bei der Einzelfeld-Speicherung muss das
 * immer laufen, wenn gross_amount, skonto_percent, skonto_days oder invoice_date
 * angefasst wurden — sonst zeigt die Skonto-Zeile dauerhaft veraltete Werte.
 * skonto_amount ist der ABZUGSBETRAG, nicht der Zahlbetrag.
 */
export function computeSkonto(src: {
  gross_amount?: number | null
  skonto_percent?: number | null
  skonto_days?: number | null
  skonto_date?: string | null
  skonto_amount?: number | null
  invoice_date?: string | null
}): { skonto_amount: number | null; skonto_date: string | null } {
  // Kein Satz = kein Skonto. Beides faellt weg, sonst bleibt eine Frist ohne
  // Rabatt stehen und die Liste zeigt weiter eine gruene Skontozeile.
  if (!src.skonto_percent) return { skonto_amount: null, skonto_date: null }

  // Der Abzugsbetrag wird NICHT aus Brutto × Satz neu gerechnet, wenn schon
  // einer da ist: der stammt aus dem Beleg und beruecksichtigt nicht
  // skontierfaehige Positionen. Sonepar weist bei 6.590,45 € Brutto einen
  // Skonto-Betrag von 47,41 € aus — gerechnet waeren es 197,71 €, und wer den
  // zoege, zahlt zu wenig (#125).
  const skonto_amount = src.skonto_amount != null
    ? src.skonto_amount
    : (src.gross_amount ? Math.round(src.gross_amount * (src.skonto_percent / 100) * 100) / 100 : null)

  // Frist: aus Skontotagen ableiten, wenn welche da sind. Sonst das im Beleg
  // ausgedruckte Datum BEHALTEN — die meisten Lieferanten nennen gar keine
  // Tage, und ein blindes Ueberschreiben mit null loeschte die Frist bei jeder
  // Bearbeitung eines anderen Feldes.
  let skonto_date = src.skonto_date ?? null
  if (src.skonto_days && src.invoice_date) {
    // Mittag Ortszeit + lokales Format — new Date('YYYY-MM-DD') ist UTC-Mitternacht,
    // toISOString() lag ueber die Zeitumstellung im Maerz einen Tag daneben.
    const d = new Date(`${src.invoice_date.slice(0, 10)}T12:00:00`)
    if (!Number.isNaN(d.getTime())) {
      d.setDate(d.getDate() + src.skonto_days)
      skonto_date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    }
  }

  return { skonto_amount, skonto_date }
}

/**
 * #82 Offener Betrag = Brutto − verrechnete Gutschriften − bisherige Teilzahlungen.
 * #107: Kann bei Gutschriften negativ werden — NICHT auf 0 klemmen, der Aufrufer
 * entscheidet, ob er das tut.
 */
export function openAmount(
  inv?: Pick<Invoice, 'gross_amount' | 'paid_amount'> | null,
  appliedCredits: { applied_amount?: number | null }[] = [],
): number {
  if (!inv) return 0
  const credits = appliedCredits.reduce((s, a) => s + (a.applied_amount || 0), 0)
  return Math.round(((inv.gross_amount || 0) - credits - (inv.paid_amount || 0)) * 100) / 100
}

/** Tage bis zur Faelligkeit; negativ = ueberfaellig. null, wenn kein Datum gesetzt. */
export function daysUntilDue(dueDate?: string | null): number | null {
  if (!dueDate) return null
  const due = new Date(dueDate)
  if (Number.isNaN(due.getTime())) return null
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  due.setHours(0, 0, 0, 0)
  return Math.round((due.getTime() - today.getTime()) / 86400000)
}

/**
 * Beleg-URL. Der Pfad kommt roh aus der DB und traegt regelmaessig Umlaute und "&"
 * ("documents/Bär_&_Ollenroth_KG_170626_7866869.pdf") — unkodiert antwortet der
 * Static-Handler darauf mit 404.
 *
 * Segmentweise encodeURIComponent statt encodeURI: letzteres laesst "#" und "?"
 * stehen, was einen kuenftigen Dateinamen still zu Fragment bzw. Query machen
 * wuerde. Der Trenner "/" muss dabei erhalten bleiben.
 */
export function fileUrl(path?: string | null): string | null {
  if (!path) return null
  const encoded = path.split('/').map(encodeURIComponent).join('/')
  return `/api/uploads/${encoded}`
}

export const isPdfFile = (inv?: Pick<Invoice, 'original_file_name' | 'original_file_path'> | null) =>
  !!inv && /\.pdf$/i.test(inv.original_file_name || inv.original_file_path || '')

export type { Invoice, InvoiceStatus, InvoiceType }
